<script lang="ts">
  import { onMount, untrack } from 'svelte';
  import { sankey, sankeyLinkHorizontal } from 'd3-sankey';
  import { sumBy } from '../lib/utils/array-helpers';

  type SummaryStats = {
    income?: number;
    amount?: number;
    expenses?: number;
  };

  type Summary = {
    byPerson: Record<string, SummaryStats>;
    byCategory: Record<string, SummaryStats>;
  };

  let { summary }: { summary: Summary } = $props();
  let svgElement: SVGSVGElement;
  let containerElement: HTMLDivElement;

  let width = $state(800);
  let height = $state(500);
  const margin = { top: 10, right: 200, bottom: 10, left: 150 };

  // Make diagram responsive
  function updateDimensions() {
    if (containerElement) {
      const containerWidth = containerElement.offsetWidth;
      width = Math.max(600, Math.min(containerWidth, 1200));

      // Calculate height based on number of major categories (>1% of total)
      if (summary?.byCategory) {
        const allCategories = Object.entries(summary.byCategory)
          .filter(([, stats]) => stats.amount && stats.amount > 0);

        const totalSpending = sumBy(allCategories, ([, stats]) => stats.amount || 0);
        const onePercentThreshold = totalSpending * 0.01;

        const majorCategoryCount = allCategories.filter(([, stats]) =>
          (stats.amount || 0) >= onePercentThreshold
        ).length;

        // Add 1 for "All Others" if there are any small categories
        const hasSmallCategories = allCategories.some(([, stats]) =>
          (stats.amount || 0) < onePercentThreshold
        );
        const categoryCount = majorCategoryCount + (hasSmallCategories ? 1 : 0);

        // Increase height to accommodate categories (minimum 45px per category)
        const minHeight = Math.max(500, categoryCount * 45);
        height = Math.min(minHeight, 1200);
      } else {
        height = 500;
      }
    }
  }

  onMount(() => {
    // Update dimensions first
    updateDimensions();

    // Render initial diagram
    renderDiagram();

    // Add resize listener
    const resizeObserver = new ResizeObserver(() => {
      updateDimensions();
      renderDiagram(); // Re-render when dimensions change
    });
    if (containerElement) {
      resizeObserver.observe(containerElement);
    }

    return () => resizeObserver.disconnect();
  });

  // Render diagram reactively when summary changes (but not when width/height change)
  $effect(() => {
    // Track summary changes
    if (summary) {
      // Use untrack to prevent tracking width/height in the render function
      untrack(() => renderDiagram());
    }
  });

  function renderDiagram() {
    // Guard: Check if we have the required elements and data
    if (!svgElement || !summary || !summary.byPerson || !summary.byCategory) {
      return;
    }

    // Create nodes and links for the Sankey diagram
    const nodes: Array<{ name: string; layer: number }> = [];
    const links: Array<{ source: number; target: number; value: number }> = [];
    const nodeMap = new Map<string, number>();

    let nodeIndex = 0;

    // Helper to get or create node
    function getNodeIndex(name: string, layer: number): number {
      const key = `${layer}-${name}`;
      if (!nodeMap.has(key)) {
        nodeMap.set(key, nodeIndex);
        nodes.push({ name, layer });
        nodeIndex++;
      }
      return nodeMap.get(key)!;
    }

    // Layer 1: Income sources (by person)
    Object.entries(summary.byPerson).forEach(([person, stats]) => {
      if (stats.income > 0) {
        const sourceIndex = getNodeIndex(`${person} Income`, 0);
        const totalIndex = getNodeIndex('Total Available', 1);
        links.push({
          source: sourceIndex,
          target: totalIndex,
          value: stats.income
        });
      }
    });

    // Layer 2: Total Available to Categories (exclude income categories)
    const allCategories = Object.entries(summary.byCategory)
      .filter(([name, stats]) => {
        // Exclude income-related categories
        const lowerName = name.toLowerCase();
        const isIncomeCategory = lowerName.includes('income') ||
                                lowerName.includes('salary') ||
                                lowerName.includes('wages') ||
                                lowerName.includes('interest') ||
                                lowerName.includes('dividend') ||
                                lowerName.includes('refund') ||
                                lowerName.includes('reimbursement');

        return stats.amount && stats.amount > 0 && !isIncomeCategory;
      })
      .sort(([, a], [, b]) => (b.amount || 0) - (a.amount || 0));

    // Calculate total spending
    const totalSpending = sumBy(allCategories, ([, stats]) => stats.amount || 0);
    const onePercentThreshold = totalSpending * 0.01;

    // Separate major categories from small ones (max 12)
    const majorCategories: Array<[string, SummaryStats]> = [];
    let allOthersTotal = 0;

    allCategories.forEach(([category, stats]) => {
      const amount = stats.amount || 0;
      // Show top 20 categories that are >= 1% of total
      if (amount >= onePercentThreshold && majorCategories.length < 20) {
        majorCategories.push([category, stats]);
      } else {
        allOthersTotal += amount;
      }
    });

    const totalIndex = getNodeIndex('Total Available', 1);

    // Add major categories
    majorCategories.forEach(([category, stats]) => {
      const categoryIndex = getNodeIndex(category, 2);
      links.push({
        source: totalIndex,
        target: categoryIndex,
        value: stats.amount || 0
      });
    });

    // Add "All Others" if there are small categories
    if (allOthersTotal > 0) {
      const othersIndex = getNodeIndex('All Others', 2);
      links.push({
        source: totalIndex,
        target: othersIndex,
        value: allOthersTotal
      });
    }

    // Guard: Don't render if there are no links
    if (links.length === 0 || nodes.length === 0) {
      return;
    }

    // Create the Sankey layout
    const sankeyGenerator = sankey()
      .nodeWidth(15)
      .nodePadding(10)
      .extent([[margin.left, margin.top], [width - margin.right, height - margin.bottom]]);

    const { nodes: sankeyNodes, links: sankeyLinks } = sankeyGenerator({
      nodes: nodes.map(d => ({ ...d })),
      links: links.map(d => ({ ...d }))
    });

    const getNodeColor = (node: any) => {
      if (node.name.includes('Income')) return 'var(--color-success)';
      if (node.name === 'Total Available') return 'var(--color-primary-bright)';
      return 'var(--color-danger)';
    };

    // Clear SVG
    while (svgElement.firstChild) {
      svgElement.removeChild(svgElement.firstChild);
    }

    // Create SVG namespace
    const svgNS = 'http://www.w3.org/2000/svg';

    // Draw links
    sankeyLinks.forEach((link: any) => {
      const path = document.createElementNS(svgNS, 'path');
      path.setAttribute('d', sankeyLinkHorizontal()(link));
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke', 'rgba(156, 163, 175, 0.3)');
      path.setAttribute('stroke-width', String(Math.max(1, link.width)));
      path.classList.add('sankey-link');

      const title = document.createElementNS(svgNS, 'title');
      title.textContent = `${link.source.name} → ${link.target.name}\n${formatCurrency(link.value)}`;
      path.appendChild(title);

      svgElement.appendChild(path);
    });

    // Draw nodes
    sankeyNodes.forEach((node: any) => {
      const group = document.createElementNS(svgNS, 'g');

      // Node rectangle
      const rect = document.createElementNS(svgNS, 'rect');
      rect.setAttribute('x', String(node.x0));
      rect.setAttribute('y', String(node.y0));
      rect.setAttribute('width', String(node.x1 - node.x0));
      rect.setAttribute('height', String(node.y1 - node.y0));
      rect.setAttribute('fill', getNodeColor(node));
      rect.classList.add('sankey-node');

      const title = document.createElementNS(svgNS, 'title');
      title.textContent = `${node.name}\n${formatCurrency(node.value)}`;
      rect.appendChild(title);

      group.appendChild(rect);

      // Node label with name and amount on same line
      const text = document.createElementNS(svgNS, 'text');
      const isLeftSide = node.x0 < width / 2;
      const xPos = isLeftSide ? node.x1 + 6 : node.x0 - 6;
      const yPos = (node.y1 + node.y0) / 2;

      text.setAttribute('x', String(xPos));
      text.setAttribute('y', String(yPos));
      text.setAttribute('dy', '0.35em');
      text.setAttribute('text-anchor', isLeftSide ? 'start' : 'end');
      text.setAttribute('fill', 'rgb(156, 163, 175)');
      text.setAttribute('font-size', '12px');

      // Create name part (bold)
      const nameTspan = document.createElementNS(svgNS, 'tspan');
      nameTspan.setAttribute('font-weight', '500');
      nameTspan.textContent = node.name;
      text.appendChild(nameTspan);

      // Create amount part (lighter, in parentheses)
      const amountTspan = document.createElementNS(svgNS, 'tspan');
      amountTspan.setAttribute('fill', 'rgb(107, 114, 128)');
      amountTspan.textContent = ` (${formatCurrency(node.value)})`;
      text.appendChild(amountTspan);

      group.appendChild(text);
      svgElement.appendChild(group);
    });
  }

  function formatCurrency(value: number) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }
</script>

<div class="sankey-container" bind:this={containerElement}>
  <svg bind:this={svgElement} {width} {height} viewBox="0 0 {width} {height}" preserveAspectRatio="xMidYMid meet"></svg>
</div>

<style>
  .sankey-container {
    width: 100%;
    overflow-x: auto;
  }

  svg {
    max-width: 100%;
    height: auto;
  }

  :global(.sankey-link) {
    transition: stroke-opacity 0.2s ease;
  }

  :global(.sankey-link:hover) {
    stroke-opacity: 0.8 !important;
  }

  :global(.sankey-node) {
    transition: opacity 0.2s ease;
    cursor: pointer;
  }

  :global(.sankey-node:hover) {
    opacity: 0.8;
  }

  @media (max-width: 768px) {
    .sankey-container {
      overflow-x: auto;
      -webkit-overflow-scrolling: touch;
    }

    svg {
      min-width: 500px;
    }
  }

  @media (max-width: 640px) {
    svg {
      min-width: 400px;
    }
  }
</style>
